<template>
  <div
    class="job-apply-card card-neumorphic !p-5 md:!p-6 relative overflow-hidden"
    :class="{ 'lg:sticky lg:top-24': sticky }"
  >
    <div
      v-if="isSubmitted"
      class="absolute inset-0 bg-light-purple/95 z-20 flex items-center justify-center text-center text-white p-6"
      style="border-radius: 24px"
    >
      <div>
        <CheckCircle :size="48" class="mx-auto mb-3" />
        <h3 class="text-xl font-semibold mb-1">You're in the network</h3>
        <p class="text-sm text-white/90">We'll call you when a camp matches your district.</p>
      </div>
    </div>

    <p class="text-xs font-semibold tracking-wide uppercase text-light-purple mb-1">
      Always open
    </p>
    <h3 class="text-xl font-poppins font-semibold text-dark-gray mb-1">
      Join Homvisit Network
    </h3>
    <p class="text-sm text-dark-gray/70 mb-4">
      4 fields. Under 30 seconds. No resume upload.
    </p>

    <form @submit.prevent="handleSubmit" class="space-y-3">
      <div>
        <label class="block text-xs font-medium text-dark-gray/70 mb-1">Name *</label>
        <input
          v-model.trim="form.name"
          type="text"
          required
          autocomplete="name"
          class="input-neumorphic !py-3 !px-4"
          placeholder="Your full name"
        />
      </div>

      <div>
        <label class="block text-xs font-medium text-dark-gray/70 mb-1">Phone *</label>
        <input
          v-model.trim="form.phone"
          type="tel"
          required
          inputmode="tel"
          autocomplete="tel"
          pattern="[0-9+\s-]{10,15}"
          class="input-neumorphic !py-3 !px-4"
          placeholder="10-digit mobile"
        />
      </div>

      <div>
        <label class="block text-xs font-medium text-dark-gray/70 mb-1">District *</label>
        <select v-model="form.district" required class="input-neumorphic !py-3 !px-4">
          <option disabled value="">Select district</option>
          <option v-for="d in districts" :key="d" :value="d">{{ d }}</option>
        </select>
      </div>

      <div>
        <label class="block text-xs font-medium text-dark-gray/70 mb-1">Experience *</label>
        <select v-model="form.experience" required class="input-neumorphic !py-3 !px-4">
          <option disabled value="">Select experience</option>
          <option value="Fresher">Fresher</option>
          <option value="0-1 years">0–1 years</option>
          <option value="1-3 years">1–3 years</option>
          <option value="3+ years">3+ years</option>
        </select>
      </div>

      <p v-if="error" class="text-sm text-red-600">{{ error }}</p>
      <a
        v-if="error && waUrl"
        :href="waUrl"
        target="_blank"
        rel="noopener"
        class="block text-center text-sm font-semibold text-light-purple underline"
      >
        Or send details on WhatsApp →
      </a>

      <button
        type="submit"
        class="w-full btn-neumorphic !py-3 text-light-purple hover:text-white hover:bg-light-purple disabled:opacity-60"
        :disabled="isSubmitting"
      >
        {{ isSubmitting ? 'Submitting…' : 'Submit & Join' }}
      </button>

      <p class="text-[11px] text-dark-gray/50 text-center leading-snug">
        By submitting, you agree we may contact you on WhatsApp/call about camp assignments.
      </p>
    </form>
  </div>
</template>

<script>
import { reactive, ref, watch } from 'vue'
import { CheckCircle } from 'lucide-vue-next'
import { DISTRICT_OPTIONS } from '../config/locations'
import { submitForm, whatsappPrefillUrl } from '../services/submitForm'

export default {
  name: 'JobApplyForm',
  components: { CheckCircle },
  props: {
    sticky: { type: Boolean, default: true },
    defaultDistrict: { type: String, default: '' },
    sourcePage: { type: String, default: 'jobs' },
  },
  setup(props) {
    const isSubmitting = ref(false)
    const isSubmitted = ref(false)
    const error = ref('')
    const waUrl = ref('')
    const form = reactive({
      name: '',
      phone: '',
      district: props.defaultDistrict || '',
      experience: '',
    })

    watch(
      () => props.defaultDistrict,
      (v) => {
        if (v && !form.district) form.district = v
      }
    )

    const handleSubmit = async () => {
      error.value = ''
      waUrl.value = ''
      isSubmitting.value = true
      try {
        await submitForm({
          subject: `Phlebo network join — ${form.district}`,
          formName: 'Join Homvisit Network',
          data: {
            name: form.name,
            phone: form.phone,
            district: form.district,
            experience: form.experience,
            source_page: props.sourcePage,
            intent: 'phlebotomist_network_application',
          },
        })
        isSubmitted.value = true
        form.name = ''
        form.phone = ''
        form.experience = ''
        setTimeout(() => {
          isSubmitted.value = false
        }, 5000)
      } catch (e) {
        error.value =
          e.message ||
          'Could not submit. Please call 75 9409 9409 or WhatsApp us.'
        waUrl.value = whatsappPrefillUrl([
          'Homvisit phlebo network join',
          `Name: ${form.name}`,
          `Phone: ${form.phone}`,
          `District: ${form.district}`,
          `Experience: ${form.experience}`,
        ])
      } finally {
        isSubmitting.value = false
      }
    }

    return {
      form,
      districts: DISTRICT_OPTIONS,
      isSubmitting,
      isSubmitted,
      error,
      waUrl,
      handleSubmit,
    }
  },
}
</script>

<style scoped>
.job-apply-card:hover {
  transform: none;
}
</style>
